import { createContext, useContext, type ReactNode } from "react"

/**
 * Chrome the host app pins around the lesson title — in the planner, the lesson drag grip and
 * the new-lesson indicator.
 *
 * Separate from UnifiedTiptapUiContext to (1) provide it where
 * the chrome is defined and (2) avoid rerendering that context when the chrome changes.
 */
export interface LessonTitleChrome {
  /** Pinned in the lesson title's left gutter. */
  grip: ReactNode
  /** Pinned to the lesson title's trailing edge. */
  trailing: ReactNode
}

/** No host chrome — what an editor with no provider (template, unit, read-only) renders. */
const NO_CHROME: LessonTitleChrome = { grip: null, trailing: null }

const LessonTitleChromeContext = createContext<LessonTitleChrome>(NO_CHROME)

/**
 * Provided by the host that owns the chrome, around its editor. Memoize the value: an unstable
 * one re-renders the lesson title on every host render.
 */
export const LessonTitleChromeProvider = LessonTitleChromeContext.Provider

/** The host's lesson-title chrome. Empty (renders nothing) outside a provider. */
export function useLessonTitleChrome(): LessonTitleChrome {
  return useContext(LessonTitleChromeContext)
}
